import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient
} from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import noSubscriptions from 'assets/images/no-subscriptions.png'
import { IconClose } from 'assets/svg/sprite'
import SearchField from 'components/Form/SearchField/SearchField'
import AvatarHolder from 'components/UI/AvatarHolder/AvatarHolder'
import { usePageTitle } from 'context/pageTitleContext'
import CreatorsList from 'features/CreatorsList/CreatorsList'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import { useDebounce } from 'helpers/hooks'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    deleteCreatorsRecentSearch,
    getCreators,
    getCreatorsRecentSearch,
    getFreeCreators,
    getLiveCreators,
    getTrendingCreators
} from 'services/endpoints/creator'
import { getMediaAutocomplete } from 'services/endpoints/media'
import { creatorsImpression } from 'services/endpoints/tracking'
import { useUserProfile } from 'store'
import { useBrowseCreatorsCacheStore } from 'store/browseCreatorsCacheStore'

import style from './_creators.module.scss'

interface RecentSearchUser {
    id: string
    name: string
    username: string
    avatar: string | { url?: string }
    searchId: string
}
interface AutocompleteUser {
    id: string
    name: string
    username: string
    avatarSrc: string
}

export const Creators = () => {
    const userData = useUserProfile()
    const { setTitle } = usePageTitle()
    const [searchSubmit, setSearchSubmit] = useState('')
    const [activeFilter, setActiveFilter] = useState('forYou')
    const [recentSearchOpen, setRecentSearchOpen] = useState(false)
    const [creators, setCreators] = useState<any>([])

    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const searchRef = useRef<any>(null)
    const { t } = useTranslation()

    const { getCache, setCache, getTimestamp } = useBrowseCreatorsCacheStore()
    const cacheKey = `${activeFilter}-${searchSubmit.trim() || 'default'}`
    const cachedData = getCache(cacheKey)
    const cacheTimestamp = getTimestamp(cacheKey)
    const isCacheFresh =
        cacheTimestamp && Date.now() - cacheTimestamp < 24 * 60 * 60 * 1000

    const [autocompleteTerm, setAutocompleteTerm] = useState('')
    const [shouldAutocomplete, setShouldAutocomplete] = useState(false)
    const debouncedAutocompleteTerm = useDebounce(autocompleteTerm, 500)

    const impressedCreatorIdsRef = useRef<Set<string>>(new Set())
    const pendingImpressionIdsRef = useRef<Set<string>>(new Set())
    const impressionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const filters = [
        { label: t('forYou'), value: 'forYou' },
        { label: t('free'), value: 'free' },
        { label: t('trending'), value: 'trending' }
    ]

    const { data: autocompleteData } = useQuery<{
        data: { users: AutocompleteUser[] }
    }>(
        ['getCreatorsAutocomplete', debouncedAutocompleteTerm],
        async () =>
            await getMediaAutocomplete({
                searchTerm: debouncedAutocompleteTerm
            }),
        { enabled: !!debouncedAutocompleteTerm && shouldAutocomplete }
    )

    const autocompleteResults: AutocompleteUser[] =
        autocompleteData?.data?.users ?? []

    useEffect(() => {
        setTitle('Browse')
    }, [setTitle])

    useEffect(() => {
        searchRef.current?.focus()
    }, [searchSubmit])

    const fetchers: Record<string, any> = {
        free: getFreeCreators,
        forYou: getCreators,
        trending: getTrendingCreators,
        live: getLiveCreators
    }

    const trimmedSearch = searchSubmit.trim() || null

    useEffect(() => {
        if (isCacheFresh && cachedData?.length) {
            setCreators(cachedData)
        }
    }, [cacheKey])

const flushCreatorImpressions = useCallback(() => {
    const ids = Array.from(pendingImpressionIdsRef.current)

    if (!ids.length) return

    pendingImpressionIdsRef.current.clear()

    console.log('[creator impressions] sending', {
        ids,
        tab: activeFilter
    })

    creatorsImpression(ids, activeFilter).catch(error => {
        console.error('[creator impressions] failed', error)
    })
}, [activeFilter])

const queueCreatorImpression = useCallback(
    (creatorId?: string) => {
        if (!creatorId) return
        if (impressedCreatorIdsRef.current.has(creatorId)) return

        impressedCreatorIdsRef.current.add(creatorId)
        pendingImpressionIdsRef.current.add(creatorId)

        console.log('[creator impressions] queued', {
            creatorId,
            pending: Array.from(pendingImpressionIdsRef.current)
        })

        // Important: only create ONE timer.
        // Do not clear/restart it on every card.
        if (!impressionTimerRef.current) {
            impressionTimerRef.current = setTimeout(() => {
                impressionTimerRef.current = null
                flushCreatorImpressions()
            }, 2500)
        }
    },
    [flushCreatorImpressions]
)

useEffect(() => {
    return () => {
        if (impressionTimerRef.current) {
            clearTimeout(impressionTimerRef.current)
            impressionTimerRef.current = null
        }

        flushCreatorImpressions()
    }
}, [flushCreatorImpressions])

useEffect(() => {
    impressedCreatorIdsRef.current.clear()
    pendingImpressionIdsRef.current.clear()

    if (impressionTimerRef.current) {
        clearTimeout(impressionTimerRef.current)
        impressionTimerRef.current = null
    }
}, [activeFilter, searchSubmit])

    const { hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } =
        useInfiniteQuery(
            ['creators', activeFilter, searchSubmit],
            async ({ pageParam = 1 }) => {
                const fetchFn = fetchers[activeFilter]
                return fetchFn({ page: pageParam, search: trimmedSearch })
            },
            {
                getNextPageParam: lastPage => {
                    const current =
                        lastPage.meta?.current_page ?? lastPage.current_page
                    const last =
                        lastPage.meta?.last_page ?? lastPage.last_page

                    if (current && last && current < last) return current + 1
                },
                onSuccess: resp => {
                    const merged = resp.pages.flatMap(page =>
                        page.data?.data || page.data || []
                    )

                    const newObjects = merged.map((creator: any) => ({
                        coverUrl: creator.cover,
                        avatarUrl: creator.avatar,
                        name: creator.name,
                        handle: creator.username,
                        isLive: creator.live_status || false,
                        isOnline: creator.online_status || false,
                        isActive: true,
                        price: 14.99,
                        save: 30,
                        userId: creator.id
                    }))

                    setCreators(newObjects)
                    setCache(cacheKey, newObjects)
                    queryClient.invalidateQueries('creators-recent-search')
                }
            }
        )

    const { data: recentSearches, refetch: refetchRecentSearches } = useQuery(
        'creators-recent-search',
        getCreatorsRecentSearch,
        { enabled: recentSearchOpen }
    )

    const observer = useRef<IntersectionObserver>()

    const lastCreatorRef = useCallback(
        (node: any) => {
            if (isFetchingNextPage || isLoading) return

            if (observer.current) observer.current.disconnect()

            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting && hasNextPage) {
                    fetchNextPage()
                }
            })

            if (node) observer.current.observe(node)
        },
        [isFetchingNextPage, isLoading, hasNextPage, fetchNextPage]
    )

const removeSearchResult = useMutation(
    async (idArray: string[]) => {
        await deleteCreatorsRecentSearch({
            search_id: idArray
        })
    },
    {
        onSuccess: () => {
            queryClient.invalidateQueries('creators-recent-search')
        }
    }
)

const filteredRecentSearches: RecentSearchUser[] =
    recentSearches?.flatMap(
        (recent: { id: string; response?: any[] }) =>
            (recent.response ?? []).map(data => ({
                ...data,
                searchId: recent.id
            }))
    ) ?? []

    const showRecentSearches =
        !autocompleteTerm.trim() &&
        recentSearchOpen &&
        filteredRecentSearches.length > 0

    const showAutocomplete =
        !!autocompleteTerm.trim() && autocompleteResults.length > 0

    return (
        <>
            <WithHeaderSection
                customClass='browse-creators-whs'
                headerSection={
                    <>
                        <div
                            tabIndex={0}
                            ref={searchRef}
                            style={{ opacity: 0, outline: 'none' }}
                        />

                        <div
                            className={style.search_wrapper}
                            onFocus={() => {
                                setRecentSearchOpen(true)
                                refetchRecentSearches()
                            }}
                            onBlur={() => setRecentSearchOpen(false)}
                            tabIndex={0}
                        >
                            <SearchField
                                value={autocompleteTerm}
                                changeFn={term => {
                                    if (!shouldAutocomplete) {
                                        setShouldAutocomplete(true)
                                    }

                                    setAutocompleteTerm(term)
                                }}
                                clearFn={() => {
                                    setAutocompleteTerm('')
                                    setShouldAutocomplete(false)
                                }}
                                customClass={`${style.search_field} ${
                                    showAutocomplete || showRecentSearches
                                        ? style.search_field_open
                                        : ''
                                }`}
                                additionalProps={{
                                    placeholder: t('searchCreators')
                                }}
                                searchSettings={{
                                    isForm: true,
                                    formSubmitFn: e => {
                                        e.preventDefault()
                                        setSearchSubmit(autocompleteTerm)
                                    },
                                    iconClickFn: () => {
                                        setSearchSubmit(autocompleteTerm)
                                    }
                                }}
                            />

                            {(showAutocomplete || showRecentSearches) && (
                                <div
                                    className={
                                        style.search_suggestions_container +
                                        ' ' +
                                        (recentSearchOpen
                                            ? style.search_suggestions_container_open
                                            : '')
                                    }
                                >
                                    <div
                                        className={
                                            style.search_suggestions_results
                                        }
                                    >
                                        {showAutocomplete ? (
                                            autocompleteResults.map(user => (
                                                <div
                                                    key={user.id}
                                                    className={
                                                        style.search_suggestion
                                                    }
                                                    onMouseDown={e => {
                                                        e.preventDefault()
                                                        navigate(
                                                            `/${user.username}`
                                                        )
                                                    }}
                                                    style={{
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <AvatarHolder
                                                        img={user.avatarSrc}
                                                        size='36'
                                                        userId={user.id}
                                                    />

                                                    <div
                                                        className={
                                                            style.search_suggestions_username_container
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                style.search_suggestions_name
                                                            }
                                                        >
                                                            {user.name}
                                                        </div>
                                                        <div
                                                            className={
                                                                style.search_suggestions_username
                                                            }
                                                        >
                                                            @{user.username}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <>
                                                <div
                                                    className={
                                                        style.search_suggestions_title_container
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            style.search_suggestions_title
                                                        }
                                                    >
                                                        {t('recentSearches')}
                                                    </div>

                                                    <div
                                                        className={
                                                            style.search_suggestions_clear
                                                        }
                                                        onMouseDown={e => {
                                                            e.preventDefault()
                                                            e.stopPropagation()

                                                            const idArray =
                                                                filteredRecentSearches.map(
                                                                    item =>
                                                                        item.searchId
                                                                )

                                                            removeSearchResult.mutate(
                                                                idArray
                                                            )
                                                        }}
                                                    >
                                                        {t('clear')}
                                                    </div>
                                                </div>

                                                <div
                                                    className={
                                                        style.search_suggestions_separator
                                                    }
                                                />

                                                {filteredRecentSearches.map(
                                                    data => (
                                                        <div
                                                            key={data.id}
                                                            className={
                                                                style.search_suggestion
                                                            }
                                                            style={{
                                                                textDecoration:
                                                                    'none',
                                                                color: 'inherit',
                                                                cursor: 'pointer'
                                                            }}
                                                            tabIndex={0}
                                                            onMouseDown={e => {
                                                                e.preventDefault()
                                                                navigate(
                                                                    `/${data.username}`
                                                                )
                                                            }}
                                                        >
                                                            <AvatarHolder
                                                                img={
                                                                    typeof data.avatar ===
                                                                    'string'
                                                                        ? data.avatar
                                                                        : data
                                                                              .avatar
                                                                              ?.url ??
                                                                          ''
                                                                }
                                                                size='36'
                                                                userId={
                                                                    data.id
                                                                }
                                                            />

                                                            <div
                                                                className={
                                                                    style.search_suggestions_username_container
                                                                }
                                                            >
                                                                <div
                                                                    className={
                                                                        style.search_suggestions_name
                                                                    }
                                                                >
                                                                    {data.name}
                                                                </div>

                                                                <div
                                                                    className={
                                                                        style.search_suggestions_username
                                                                    }
                                                                >
                                                                    @
                                                                    {
                                                                        data.username
                                                                    }
                                                                </div>
                                                            </div>

                                                            <div
                                                                className={
                                                                    style.search_suggestions_remove_button
                                                                }
                                                                onMouseDown={e => {
                                                                    e.preventDefault()
                                                                    e.stopPropagation()

                                                                    removeSearchResult.mutate(
                                                                        [
                                                                            data.searchId
                                                                        ]
                                                                    )
                                                                }}
                                                            >
                                                                <IconClose color='#778797' />
                                                            </div>
                                                        </div>
                                                    )
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                }
            >
                <>
                    <Wrapper>
                        {filters.map((navLink, key) => (
                            <Item
                                key={key}
                                $active={navLink.value === activeFilter}
                                onClick={() => setActiveFilter(navLink.value)}
                            >
                                {navLink.label}
                            </Item>
                        ))}
                    </Wrapper>
                        <CreatorsList
                            creators={creators}
                            customClass={style.browse_creators_list}
                            renderLocation='creators'
                            creatorRef={lastCreatorRef}
                            customPlaceholderText={t('creatorsAppearHere')}
                            tab={activeFilter}
                            isLoading={isLoading}
                            onCreatorImpression={queueCreatorImpression}
                        />

                </>
            </WithHeaderSection>

            <DesktopAdditionalContent />
        </>
    )
}

const Wrapper = styled.div`
    align-items: center;
    border-radius: 2rem;
    display: flex;
    height: 4rem;
    padding: 0.5rem;
    background-color: #f5f5f6;

    @media (min-width: 950px) {
        width: fit-content;
    }
`

interface ItemProps {
    $active: boolean
}

const Item = styled.span<ItemProps>`
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    align-items: center;
    border-radius: 2rem;
    display: flex;
    font-family: Montserrat, sans-serif;
    font-size: 1.2rem;
    font-style: normal;
    font-weight: 600;
    height: 100%;
    justify-content: center;
    letter-spacing: 0.02em;
    line-height: 1.5rem;
    overflow: hidden;
    padding: 0 2rem;
    text-decoration: none;
    text-overflow: ellipsis;
    text-transform: capitalize;
    white-space: nowrap;
    width: fit-content;
    color: #757576;
    transition: all 0.2s;
    ${props => (props.$active ? 'background: #262a33;' : '')}
    ${props => (props.$active ? ' color: #fff;' : '')}
`